import { useMutation, useQuery, useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  GET_LOYALTY_CATEGORIES_QUERY,
  GET_PACKAGE,
  GET_PACKAGE_LIST,
  GET_PERIOD,
  GET_PERIOD_DETAIL,
  LIST_PERIODS,
} from "graphql/loyaltyPackage/query";
import {
  CREATE_PACKAGE,
  CREATE_PERIOD,
  SWITCH_PACKAGE_STATUS,
  SWITCH_PERIOD_STATUS,
  UPDATE_PACKAGE,
  UPDATE_PERIOD,
} from "graphql/loyaltyPackage/mutation";
import {
  mapCreatePackageResponse,
  mapCreatePeriodResponse,
  mapGetPackageListResponse,
  mapGetPackageResponse,
  mapGetPeriodDetailResponse,
  mapGetPeriodResponse,
  mapListPeriodsResponse,
  mapPeriodInputToBe,
  mapPeriodListFilters,
  mapUpdatePeriodInputToBe,
  mapProgramInputToBe,
  mapProgramListFilters,
  mapProgramToListCacheItem,
  mapSwitchPackageStatusResponse,
  mapSwitchPeriodStatusResponse,
  mapUpdatePackageResponse,
  mapUpdatePeriodResponse,
} from "features/LoyaltyPackage/data/apiAdapters";
import { converterCategoriesForForm } from "hooks/product/converter";
import { useGetCreatedByWithIds } from "hooks/order";

const LOYALTY_CATEGORY_SELECT_LEVEL = 2;

const DEFAULT_PAGINATION = { offset: 0, limit: 10 };

const LIST_QUERY_FETCH_POLICY = {
  fetchPolicy: "cache-and-network",
  nextFetchPolicy: "cache-and-network",
};

/** Chi tiết CT/gói: cache-first — mutation getPackage/getPeriod không kích hoạt fetch nền list (keyArgs tách cache) */
const DETAIL_QUERY_FETCH_POLICY = {
  fetchPolicy: "cache-first",
  nextFetchPolicy: "cache-first",
};

/**
 * Trả về fetch policy cho list/detail query:
 * - Chưa có cache (lần đầu, sau reload) → "cache-first": chỉ 1 request, Apollo dedup mount #2 (StrictMode)
 * - Đã có cache (quay lại màn / đổi tab) → "cache-and-network": hiện cache ngay + fetch nền cập nhật
 */
function useStaleFetchPolicy(query, variables, { skip = false } = {}) {
  const client = useApolloClient();

  const hasCachedData = useMemo(() => {
    if (skip) return false;
    try {
      const data = client.readQuery({ query, variables });
      return Boolean(data);
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, query, JSON.stringify(variables), skip]);

  return useMemo(
    () =>
      hasCachedData
        ? { fetchPolicy: "cache-and-network", nextFetchPolicy: "cache-and-network" }
        : { fetchPolicy: "cache-first", nextFetchPolicy: "cache-and-network" },
    [hasCachedData]
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Khi BE trả createdBy null nhưng có createdById — lookup fullname qua user.pagination.
 */
export function usePeriodCreatedByNameMap(periods) {
  const lookupIds = useMemo(() => {
    const ids = (periods ?? [])
      .map((period) => {
        if (period?.createdBy?.fullname) return null;
        return period?.createdById ?? period?.createdBy?.id;
      })
      .filter(Boolean);
    return [...new Set(ids)];
  }, [periods]);

  const { data: createdByUsers } = useGetCreatedByWithIds({
    ids: lookupIds.length > 0 ? lookupIds : undefined,
  });

  return useMemo(
    () => Object.fromEntries((createdByUsers ?? []).map((user) => [user.value, user.label])),
    [createdByUsers]
  );
}

// ── Chương trình chiết khấu ──────────────────────────────────────────────────

/** Danh sách chương trình chiết khấu — dùng query GetPackageList */
export function useLoyaltyPrograms(filters, pagination = DEFAULT_PAGINATION, { skip = false } = {}) {
  const beFilters = mapProgramListFilters(filters);

  const programsFetchPolicy = useStaleFetchPolicy(GET_PACKAGE_LIST, { filters: beFilters, pagination }, { skip });

  const apolloResult = useQuery(GET_PACKAGE_LIST, {
    variables: { filters: beFilters, pagination },
    skip,
    ...programsFetchPolicy,
  });

  const data = useMemo(() => mapGetPackageListResponse(apolloResult.data), [apolloResult.data]);

  return {
    data,
    // loading chỉ true khi chưa có data (cold load). Khi cache-and-network fetch nền
    // (đã có data cũ), giữ loading=false để tránh spinner đè lên data đang hiển thị.
    loading: apolloResult.loading && !apolloResult.data,
    error: apolloResult.error,
    refetch: apolloResult.refetch,
  };
}

/** Chi tiết chương trình — dùng query GetPackage */
export function useLoyaltyProgram(id) {
  const detailFetchPolicy = useStaleFetchPolicy(GET_PACKAGE, { id }, { skip: !id });

  const apolloResult = useQuery(GET_PACKAGE, {
    variables: { id },
    skip: !id,
    ...detailFetchPolicy,
  });

  return {
    data: mapGetPackageResponse(apolloResult.data),
    loading: apolloResult.loading && !apolloResult.data,
    error: apolloResult.error,
    refetch: apolloResult.refetch,
  };
}

/**
 * Map id → title cho các CT thiếu `loyaltyPackage.title` trên listPeriods (chỉ fetch ID đang hiển thị).
 */
export function useLoyaltyProgramTitleMap(periods) {
  const client = useApolloClient();
  const [titleById, setTitleById] = useState({});

  const missingIds = useMemo(() => {
    const ids = new Set();
    (periods ?? []).forEach((period) => {
      if (period?.loyaltyPackage?.title) return;
      if (period?.loyaltyPackageId) ids.add(period.loyaltyPackageId);
    });
    return [...ids].filter((id) => !titleById[id]);
  }, [periods, titleById]);

  const missingIdsKey = missingIds.join(",");

  useEffect(() => {
    if (!missingIds.length) return;

    let cancelled = false;

    const fetchTitles = async () => {
      const entries = await Promise.all(
        missingIds.map(async (id) => {
          try {
            const result = await client.query({
              query: GET_PACKAGE,
              variables: { id },
              fetchPolicy: "cache-first",
            });
            const pkg = mapGetPackageResponse(result.data);
            return [id, pkg?.title];
          } catch {
            return [id, undefined];
          }
        })
      );

      if (cancelled) return;

      const resolved = Object.fromEntries(entries.filter(([, title]) => title));
      if (Object.keys(resolved).length) {
        setTitleById((prev) => ({ ...prev, ...resolved }));
      }
    };

    fetchTitles();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingIdsKey, client]);

  return titleById;
}

// ── Gói chiết khấu (Periods) ─────────────────────────────────────────────────

/**
 * Danh sách gói chiết khấu — dùng query listPeriods.
 * filters.loyaltyPackageId — chi tiết CT; filters.loyaltyPackageIds[] — tab Gói (BE hỗ trợ multi).
 */
export function useLoyaltyPeriods(filters, pagination = DEFAULT_PAGINATION, { staleWhileRevalidate = true } = {}) {
  const beFilters = mapPeriodListFilters(filters);

  const staleFetchPolicy = useStaleFetchPolicy(
    LIST_PERIODS,
    { filters: beFilters, pagination },
    { skip: !staleWhileRevalidate }
  );
  const periodsFetchPolicy = staleWhileRevalidate ? staleFetchPolicy : DETAIL_QUERY_FETCH_POLICY;

  const apolloResult = useQuery(LIST_PERIODS, {
    variables: { filters: beFilters, pagination },
    ...periodsFetchPolicy,
  });

  const data = useMemo(() => mapListPeriodsResponse(apolloResult.data), [apolloResult.data]);

  return {
    data,
    loading: apolloResult.loading && !apolloResult.data,
    error: apolloResult.error,
    refetch: apolloResult.refetch,
  };
}

/** Chi tiết gói chiết khấu — dùng query getPeriod */
export function useLoyaltyPeriod(id) {
  const detailFetchPolicy = useStaleFetchPolicy(GET_PERIOD, { id }, { skip: !id });

  const apolloResult = useQuery(GET_PERIOD, {
    variables: { id },
    skip: !id,
    ...detailFetchPolicy,
  });

  return {
    data: mapGetPeriodResponse(apolloResult.data),
    loading: apolloResult.loading && !apolloResult.data,
    error: apolloResult.error,
    refetch: apolloResult.refetch,
  };
}

/** Chi tiết gói chiết khấu (màn detail) — query lightweight GET_PERIOD_DETAIL, chỉ fields cần cho màn Xem chi tiết */
export function useLoyaltyPeriodDetail(id) {
  const detailFetchPolicy = useStaleFetchPolicy(GET_PERIOD_DETAIL, { id }, { skip: !id });

  const apolloResult = useQuery(GET_PERIOD_DETAIL, {
    variables: { id },
    skip: !id,
    ...detailFetchPolicy,
  });

  return {
    data: mapGetPeriodDetailResponse(apolloResult.data),
    loading: apolloResult.loading && !apolloResult.data,
    error: apolloResult.error,
    refetch: apolloResult.refetch,
  };
}

// ── Mutations — Chương trình ─────────────────────────────────────────────────

export function useCreateLoyaltyProgram() {
  const [mutate, { loading }] = useMutation(CREATE_PACKAGE);

  const create = useCallback(
    async (input) => {
      const res = await mutate({ variables: { input: mapProgramInputToBe(input) } });
      return mapCreatePackageResponse(res.data);
    },
    [mutate]
  );

  return { create, loading };
}

/** Refetch chỉ GetPackage — tránh dùng sau mutation (refetchQueries có thể kéo GetPackageList). */
export async function refetchLoyaltyProgramDetail(client, id) {
  if (!id) return;

  await client.refetchQueries({
    include: [{ query: GET_PACKAGE, variables: { id } }],
  });
}

/**
 * Refresh GetPackage sau mutation — đúng 1 request.
 * Không dùng refetchQueries (có thể kéo GetPackageList); ghi thẳng cache GetPackage.
 */
export async function refreshProgramDetailFromNetwork(client, id) {
  if (!id) return null;

  const variables = { id };
  const { data } = await client.query({
    query: GET_PACKAGE,
    variables,
    fetchPolicy: "no-cache",
  });

  client.writeQuery({ query: GET_PACKAGE, variables, data });
  return mapGetPackageResponse(data);
}

/**
 * Cập nhật 1 dòng trong cache GetPackageList từ mutation response — không gọi network.
 * Tab Chương trình có useQuery cache-and-network đang watch; fetch + writeQuery sẽ kích thêm request.
 */
export function updateProgramInListCache(client, programId, updatedProgram, filters, pagination) {
  const beFilters = mapProgramListFilters(filters);
  const variables = { filters: beFilters, pagination };

  try {
    client.readQuery({ query: GET_PACKAGE_LIST, variables });
  } catch {
    return false;
  }

  let updated = false;

  client.cache.updateQuery({ query: GET_PACKAGE_LIST, variables }, (data) => {
    const listPackages = data?.loyaltyPackage?.listPackages;
    if (!listPackages) return data;

    const packages = listPackages.packages ?? [];
    const idx = packages.findIndex((p) => p.id === programId);
    if (idx === -1) return data;

    updated = true;
    const nextPackages = packages.slice();
    nextPackages[idx] = mapProgramToListCacheItem(updatedProgram, packages[idx]);

    return {
      ...data,
      loyaltyPackage: {
        ...data.loyaltyPackage,
        listPackages: {
          ...listPackages,
          packages: nextPackages,
        },
      },
    };
  });

  return updated;
}

/** Thêm CT mới vào đầu cache trang 1 sau create — không gọi network */
export function prependProgramToListCache(client, createdProgram, filters, pagination) {
  if (pagination?.offset !== 0 || !createdProgram?.id) return false;

  const beFilters = mapProgramListFilters(filters);
  const variables = { filters: beFilters, pagination };

  try {
    client.readQuery({ query: GET_PACKAGE_LIST, variables });
  } catch {
    return false;
  }

  let prepended = false;

  client.cache.updateQuery({ query: GET_PACKAGE_LIST, variables }, (data) => {
    const listPackages = data?.loyaltyPackage?.listPackages;
    if (!listPackages) return data;

    prepended = true;
    const packages = listPackages.packages ?? [];
    const limit = pagination.limit ?? packages.length;
    const paginationData = listPackages.paginationData ?? { total: packages.length, limit, offset: 0 };
    const cacheItem = mapProgramToListCacheItem(createdProgram);

    return {
      ...data,
      loyaltyPackage: {
        ...data.loyaltyPackage,
        listPackages: {
          ...listPackages,
          paginationData: {
            ...paginationData,
            total: (paginationData.total ?? packages.length) + 1,
          },
          packages: [cacheItem, ...packages].slice(0, limit),
        },
      },
    };
  });

  return prepended;
}

export function useUpdateLoyaltyProgram() {
  const [mutate, { loading }] = useMutation(UPDATE_PACKAGE, {
    fetchPolicy: "no-cache",
  });

  const update = useCallback(
    async (id, input) => {
      const res = await mutate({ variables: { id, input: mapProgramInputToBe(input) } });
      return mapUpdatePackageResponse(res.data);
    },
    [mutate]
  );

  return { update, loading };
}

export function useSwitchLoyaltyProgramStatus() {
  const [mutate, { loading }] = useMutation(SWITCH_PACKAGE_STATUS);

  const switchStatus = useCallback(
    async (id, isActive) => {
      const res = await mutate({ variables: { id, isActive } });
      return mapSwitchPackageStatusResponse(res.data);
    },
    [mutate]
  );

  return { switchStatus, loading };
}

// ── Mutations — Gói (Period) ─────────────────────────────────────────────────

export function useCreateLoyaltyPeriod() {
  const [mutate, { loading }] = useMutation(CREATE_PERIOD);

  const create = useCallback(
    async (input) => {
      const res = await mutate({ variables: { input: mapPeriodInputToBe(input) } });
      return mapCreatePeriodResponse(res.data);
    },
    [mutate]
  );

  return { create, loading };
}

export function useUpdateLoyaltyPeriod() {
  const [mutate, { loading }] = useMutation(UPDATE_PERIOD);

  const update = useCallback(
    async (id, input) => {
      const res = await mutate({ variables: { id, input: mapUpdatePeriodInputToBe(input) } });
      return mapUpdatePeriodResponse(res.data);
    },
    [mutate]
  );

  return { update, loading };
}

export function useSwitchLoyaltyPeriodStatus() {
  const [mutate, { loading }] = useMutation(SWITCH_PERIOD_STATUS);

  const switchStatus = useCallback(
    async (id, isActive) => {
      const res = await mutate({ variables: { id, isActive } });
      const result = mapSwitchPeriodStatusResponse(res.data);
      if (!result) {
        const message = res.errors?.[0]?.message || "Không thể thay đổi trạng thái gói chiết khấu";
        throw new Error(message);
      }
      return result;
    },
    [mutate]
  );

  return { switchStatus, loading };
}

/** Ngành hàng cấp 2 (active) — API thật, pattern giống FormProduct / ModalSelectProduct */
export function useLoyaltyCategoryOptions({ skip = false } = {}) {
  const { loading, data } = useQuery(GET_LOYALTY_CATEGORIES_QUERY, {
    variables: { req: { isActive: true, levels: [1] } },
    skip,
    ...LIST_QUERY_FETCH_POLICY,
  });

  return {
    loading: skip ? false : loading,
    options: converterCategoriesForForm(data?.category?.list, LOYALTY_CATEGORY_SELECT_LEVEL) || [],
  };
}

/** Label + màu trạng thái (text only, không badge) — pattern giống useCampaign */
export function useLoyaltyPackage() {
  const { t } = useTranslation();

  const TEXT_STATUS_LOYALTY = {
    UPCOMING: <span style={{ color: "#EF9351" }}>{t("loyaltyPackage.status.upcoming")}</span>,
    ONGOING: <span style={{ color: "#32BF46" }}>{t("loyaltyPackage.status.ongoing")}</span>,
    RECONCILING: <span style={{ color: "#5b6673" }}>{t("loyaltyPackage.status.reconciling")}</span>,
    RECONCILED: <span style={{ color: "#1890FF" }}>{t("loyaltyPackage.status.reconciled")}</span>,
    COMPLETED: <span style={{ color: "#5b6673", fontWeight: 500 }}>{t("loyaltyPackage.status.completed")}</span>,
  };

  return { TEXT_STATUS_LOYALTY };
}
